import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sparkles, RefreshCw, Wand2, Target, TrendingUp, AlertCircle, Rocket, Send,
  ArrowDownRight, ArrowUpRight, Minus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const directionIcon = (d) => {
  if (d === 'positive') return <ArrowUpRight className="w-4 h-4 text-emerald-400" />;
  if (d === 'negative') return <ArrowDownRight className="w-4 h-4 text-rose-400" />;
  return <Minus className="w-4 h-4 text-amber-400" />;
};

const trendIcon = (t) => {
  if (t === 'up') return <ArrowUpRight className="w-3 h-3 text-emerald-400" />;
  if (t === 'down') return <ArrowDownRight className="w-3 h-3 text-rose-400" />;
  return <Minus className="w-3 h-3 text-amber-400" />;
};

const urgencyBadge = (u) => {
  const c = u === 'high' ? 'bg-rose-500/15 text-rose-400 border-rose-500/30'
    : u === 'medium' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
    : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
  return <Badge className={`text-[10px] ${c}`}>{u}</Badge>;
};

/**
 * AIInsightPanel — fetches a real GPT-5.2 insight for (brand, kpi_name) on mount.
 *
 * Props:
 *   brandId, brandName, kpiName, context: object (passed to LLM)
 *   className: optional
 */
export default function AIInsightPanel({ brandId, brandName, kpiName, context, className = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = async (forceRefresh = false) => {
    if (!brandId || !kpiName) return;
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-insights/${kpiName}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brand_id: brandId, brand_name: brandName, context, force_refresh: forceRefresh }),
      });
      const j = await res.json();
      if (j.success) setData(j.insight);
      else setError(j.insight?.na_reason || 'Failed to generate insight');
    } catch (e) {
      setError('Network error: ' + (e?.message || e));
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [brandId, kpiName]);

  if (loading) {
    return (
      <Card className={`bg-gradient-to-br from-violet-500/10 to-pink-500/10 border-violet-500/30 ${className}`} data-testid="ai-insight-loading">
        <CardContent className="p-5 flex items-center gap-3">
          <Wand2 className="w-5 h-5 text-violet-400 animate-pulse" />
          <div>
            <div className="font-semibold text-sm">Generating AI insight…</div>
            <div className="text-xs text-muted-foreground mt-0.5">GPT-5.2 analyzing {brandName} data</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`bg-rose-500/10 border-rose-500/30 ${className}`} data-testid="ai-insight-error">
        <CardContent className="p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-rose-200">AI insight unavailable</div>
            <div className="text-xs text-rose-100/70 mt-1">{error}</div>
            <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => load(true)}>
              <RefreshCw className="w-3 h-3 mr-1" />Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card className={`bg-gradient-to-br from-violet-500/10 to-pink-500/5 border-violet-500/30 ${className}`} data-testid="ai-insight-panel">
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <div>
              <div className="font-semibold text-sm">AI Analyst Insight</div>
              <div className="text-[10px] text-muted-foreground">
                {data.model || 'GPT-5.2'} · {data.from_cache ? `cached ${data.cache_age_hours || ''}h` : 'fresh'} · {brandName}
              </div>
            </div>
          </div>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => load(true)} disabled={refreshing} data-testid="ai-insight-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* WHAT */}
        {data.what && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {directionIcon(data.what.direction)}
              <h3 className="font-semibold text-sm">{data.what.title}</h3>
              {data.what.magnitude && (
                <Badge variant="outline" className="text-[10px] capitalize">{data.what.magnitude}</Badge>
              )}
            </div>
            <p className="text-sm text-foreground/80 leading-relaxed">{data.what.summary}</p>
          </div>
        )}

        {/* WHY */}
        {data.why && (
          <div className="space-y-2 pt-2 border-t border-border/40">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-400" />
              <h4 className="font-semibold text-xs uppercase tracking-wider">{data.why.title || 'Drivers'}</h4>
            </div>
            <p className="text-sm text-foreground/80 leading-relaxed">{data.why.summary}</p>
            {data.why.drivers && data.why.drivers.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                {data.why.drivers.map((d, i) => (
                  <div key={i} className="p-2 rounded bg-background/40 border border-border/40">
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      {trendIcon(d.trend)}{d.factor}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{d.contribution}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* IMPACT */}
        {data.impact && (
          <div className="space-y-2 pt-2 border-t border-border/40">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <h4 className="font-semibold text-xs uppercase tracking-wider">{data.impact.title || 'Impact'}</h4>
            </div>
            <p className="text-sm text-foreground/80 leading-relaxed">{data.impact.summary}</p>
          </div>
        )}

        {/* ACTIONS */}
        {data.actions && (
          <div className="space-y-2 pt-2 border-t border-border/40">
            <div className="flex items-center gap-2">
              <Rocket className="w-4 h-4 text-pink-400" />
              <h4 className="font-semibold text-xs uppercase tracking-wider">{data.actions.title || 'Recommended Actions'}</h4>
            </div>
            {['p0','p1','p2'].filter(p => data.actions[p]).map((p) => {
              const a = data.actions[p];
              const color = p === 'p0' ? 'border-rose-500/40 bg-rose-500/5' : p === 'p1' ? 'border-amber-500/40 bg-amber-500/5' : 'border-blue-500/40 bg-blue-500/5';
              return (
                <div key={p} className={`p-2.5 rounded-lg border ${color} flex items-start gap-2`}>
                  <Badge className="text-[10px] uppercase">{p}</Badge>
                  <div className="flex-1 text-sm">
                    <div>{a.action}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {a.owner && <span>Owner: <strong>{a.owner}</strong></span>}
                      {a.kpi && <span className="ml-2">KPI: <strong>{a.kpi}</strong></span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CAMPAIGN SUGGESTION */}
        {data.campaign_suggestion && (
          <div className="space-y-2 pt-2 border-t border-border/40">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-indigo-400" />
              <h4 className="font-semibold text-xs uppercase tracking-wider">Campaign Suggestion</h4>
              {data.campaign_suggestion.urgency && urgencyBadge(data.campaign_suggestion.urgency)}
            </div>
            <div className="text-sm space-y-1">
              <div><span className="text-muted-foreground">Audience:</span> {data.campaign_suggestion.segment}</div>
              <div><span className="text-muted-foreground">Channel:</span> {data.campaign_suggestion.channel}</div>
              <div><span className="text-muted-foreground">Theme:</span> {data.campaign_suggestion.message_theme}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
